"use client"

import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import { cn } from "@/lib/utils"

type ToastTone = "success" | "error" | "info"

interface ToastEntry {
  id: string
  tone: ToastTone
  title?: string
  message: string
  /** ms — defaults to 5000. Set to 0 to keep on-screen until dismissed. */
  duration: number
}

interface ToastContextValue {
  push: (input: { tone?: ToastTone; title?: string; message: string; duration?: number }) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DEFAULT_DURATION = 5000

/**
 * Global toast provider. Wrap the article reader layout once; any client
 * component then calls `useToast().push({ tone, message })` to
 * surface a confirmation/error/info notification.
 *
 * Stacks at bottom-right on desktop, full-width at the bottom on
 * mobile. Auto-dismisses after `duration` ms (default 5s); pass 0 to
 * keep until manually dismissed (rare — usually for hard errors that
 * the user must acknowledge).
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  // Keep the timers in a ref so the value identity stays stable across
  // re-renders — otherwise each push would schedule then immediately
  // see the new map and the cleanup would race.
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const t = timersRef.current.get(id)
    if (t) {
      clearTimeout(t)
      timersRef.current.delete(id)
    }
  }, [])

  const push = useCallback<ToastContextValue["push"]>(
    ({ tone = "info", title, message, duration }) => {
      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      const ms = duration ?? DEFAULT_DURATION
      setToasts((prev) => [...prev, { id, tone, title, message, duration: ms }])
      if (ms > 0) {
        const handle = setTimeout(() => dismiss(id), ms)
        timersRef.current.set(id, handle)
      }
      return id
    },
    [dismiss],
  )

  // Clear pending timers on unmount so dev hot-reloads don't leak.
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      for (const t of timers.values()) clearTimeout(t)
      timers.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-label="Notifications"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-3 sm:inset-auto sm:bottom-5 sm:right-5 sm:items-end sm:gap-2.5 sm:px-0"
      >
        {toasts.map((t) => (
          <Toast key={t.id} entry={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // No provider — bail with a no-op so unmounted/test paths don't crash.
    // Production code paths that rely on toast will mount the provider in
    // the article reader layout.
    return {
      push: () => "",
      dismiss: () => undefined,
    }
  }
  return ctx
}

function Toast({ entry, onDismiss }: { entry: ToastEntry; onDismiss: () => void }) {
  const { tone, title, message } = entry
  const Icon = tone === "success" ? CheckCircle2 : tone === "error" ? AlertTriangle : Info

  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-paper-high)] px-4 py-3 shadow-[var(--glass-shadow-2)] sm:w-[22rem]",
        tone === "error" && "border-[var(--color-vermilion)]",
        tone === "success" && "border-l-[3px] border-l-[var(--color-ink)] pl-3",
        tone === "info" && "border-l-[3px] border-l-[var(--color-ink-soft)] pl-3",
      )}
    >
      <Icon
        size={16}
        className={cn(
          "mt-0.5 shrink-0",
          tone === "error" && "text-[var(--color-vermilion)]",
          tone === "success" && "text-[var(--color-ink)]",
          tone === "info" && "text-[var(--color-ink-muted)]",
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        {title ? (
          <p className="text-[0.92rem] font-semibold leading-tight text-[var(--color-ink)]">{title}</p>
        ) : null}
        <p
          className={cn(
            "text-[0.88rem] leading-snug text-[var(--color-ink-muted)]",
            title && "mt-0.5",
          )}
        >
          {message}
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="-mr-1 -mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-ink-soft)] transition-colors hover:bg-[var(--color-paper-deep)] hover:text-[var(--color-ink)] focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-paper)]"
      >
        <X size={13} />
      </button>
    </div>
  )
}
